import { useCallback, useState, useEffect, ChangeEvent, useContext } from 'react'
import { t } from '@lingui/core/macro'
import AddIcon from '@mui/icons-material/Add'
import ClearIcon from '@mui/icons-material/Clear'
import RemoveIcon from '@mui/icons-material/Remove'
import SearchIcon from '@mui/icons-material/Search'
import {
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  Popover,
  TextField,
  Box,
  InputAdornment,
} from '@mui/material'
import { ComparisonOperatorSelect } from '@components'
import {
  DatasetDataContext,
  ActionType as DatasetDataActionType,
} from '@datasets/dataset-details/datasetData/context'
import { useDatasetQueryContext } from '@datasets/dataset-query-context/hooks'
import { QueryOptionFilter } from '@datasets/dataset-query-context/utils/queryOptions'
import { SubmissionPartial } from '@datasets/hooks/useSubmissionPartials'
import { DateOperator, canHaveValueOperand, sortOrders, sortOrderText } from '@global/constants'
import { ColumnDataType, GovernanceType, User } from '@global/types'
import { ComparisonOperatorValueField } from '../comparisonOperatorValueField'
import { SelectDate } from './selectDate'
import { SelectSubmission } from './selectSubmission'
import { SelectUser } from './selectUser'
import * as css from './styles'
import { UserOptions } from './types'
import { generateUserFilter, getColumnDataType } from './utils'
import { DbColumnName } from '../readGridToolbar/components/toggleColumns/types'
import { EventTrackingEventType, trackEvent } from 'lib/eventTracking'

const MAX_FILTER_COUNT = 5

interface Props {
  anchorEl: Element | null
  fieldId: number | string
  governanceType: GovernanceType
  rowPropertyName: string
  fieldType: string
  readGridParent: {
    _bottomLeftGrid?: {
      scrollToCell: (options: { rowIndex: number }) => void
    }
    _bottomRightGrid?: {
      scrollToCell: (options: { rowIndex: number }) => void
    }
  }
  userOptions: UserOptions
  onClose: () => void
}

export const ColumnFilterPopover = ({
  anchorEl,
  fieldId,
  governanceType,
  rowPropertyName,
  fieldType,
  readGridParent,
  userOptions,
  onClose,
}: Props) => {
  const {
    getFiltersByFieldId,
    getSortOrderByFieldId,
    getSearchTermByFieldId,
    updateQueryOptionsByFieldId,
    updateUrlParams,
    submissionPartials,
  } = useDatasetQueryContext()

  const { dispatch: dispatchDatasetData } = useContext(DatasetDataContext)

  const [filters, setFilters] = useState<any[]>(getFiltersByFieldId(String(fieldId)))
  const [sortOrder, setSortOrder] = useState(getSortOrderByFieldId(String(fieldId)))
  const [search, setSearch] = useState<string>(getSearchTermByFieldId(String(fieldId)))

  /* eslint no-underscore-dangle: ["error", { "allow": ["_bottomLeftGrid", "_bottomRightGrid"] }] */
  const scrollToTop = () => {
    // Only scroll rows; don't scroll columns
    readGridParent._bottomLeftGrid?.scrollToCell({ rowIndex: 0 })
    readGridParent._bottomRightGrid?.scrollToCell({ rowIndex: 0 })
  }

  const columnDataType = getColumnDataType(governanceType, fieldId)

  const handleClose = useCallback(() => {
    onClose()
  }, [onClose])

  const handleSearch = useCallback((e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setSearch(e.target.value)
  }, [])

  const handleFilterOperatorChange = useCallback(
    filterIndex => event => {
      const newOperator = event.target.value
      setFilters(oldFilters => {
        const newFilters = [...oldFilters]
        const newFilter = {
          ...newFilters[filterIndex],
          operator: newOperator,
        }

        if (newOperator && !canHaveValueOperand(newOperator)) {
          newFilter.value = ''
        }

        newFilters[filterIndex] = newFilter
        return newFilters
      })
    },
    [],
  )

  const handleClearSearch = useCallback(() => {
    setSearch('')
  }, [])

  const handleFilterValueChange = useCallback(
    filterIndex => event => {
      const newValue = event.target.value

      setFilters(oldFilters => {
        const newFilters = [...oldFilters]

        newFilters[filterIndex] = {
          ...newFilters[filterIndex],
          value: newValue,
        }

        return newFilters
      })
    },
    [],
  )

  const handleSortChange = useCallback(
    selectedSortOrder => () => {
      setSortOrder(prev => (prev !== selectedSortOrder ? selectedSortOrder : ''))
    },
    [],
  )

  const addFilter = useCallback(
    filterIndex => () => {
      if (filters.length === MAX_FILTER_COUNT) {
        return
      }

      setFilters(oldFilters => {
        const newFilter = new QueryOptionFilter(String(fieldId))
        const firstHalf = oldFilters.slice(0, filterIndex)
        const lastHalf = oldFilters.slice(filterIndex)
        return [...firstHalf, newFilter, ...lastHalf]
      })
    },
    [fieldId, filters],
  )

  const removeFilter = useCallback(
    filterIndex => () => {
      setFilters(oldFilters => oldFilters.filter((_, index) => index !== filterIndex))
    },
    [],
  )

  /** function to clear current selection before applying a new filter */
  const clearSelection = useCallback(() => {
    dispatchDatasetData({ type: DatasetDataActionType.setSelectedRows, payload: {} })
  }, [])

  const clearColumnQueryOptions = useCallback(() => {
    setFilters([])
    setSortOrder('')
    setSearch('')
  }, [])

  const resetColumnQueryOptions = useCallback(() => {
    setFilters(getFiltersByFieldId(String(fieldId)))
    setSortOrder(getSortOrderByFieldId(String(fieldId)))
    setSearch(getSearchTermByFieldId(String(fieldId)))
  }, [fieldId, getFiltersByFieldId, getSortOrderByFieldId])

  const applyColumnQueryOptions = useCallback(
    e => {
      e.stopPropagation()
      onClose()
      let submissionIds: number[] = []

      const submissions = Object.values<SubmissionPartial[]>(submissionPartials as any).flat()
      submissionIds = submissions
        .filter(s => s.submission_name.toLowerCase().includes(search?.toLowerCase?.()))
        .map(s => s.submission_id)

      const isUserField =
        fieldId === 'properties.updatedById' || fieldId === 'properties.createdById'

      const userFilters = isUserField ? generateUserFilter(search, fieldId, userOptions) : []

      const nextQueryOptions = updateQueryOptionsByFieldId(String(fieldId), {
        filters: [...filters, ...userFilters],
        sortOrder,
        searchTerm: isUserField && userFilters.length > 0 ? null : search,
        columnDataType,
        submissionIds,
      })
      updateUrlParams(nextQueryOptions)
      scrollToTop()
      clearSelection()

      // Pendo event tracking logic
      if (filters.length !== 0) {
        if (governanceType === 'property') {
          trackEvent(EventTrackingEventType.COLUMNS_FILTERED, {
            fieldType,
            rowPropertyName,
          })
        } else {
          trackEvent(EventTrackingEventType.COLUMNS_FILTERED, {
            fieldType,
          })
        }
      }

      const sortOrderTextOption = nextQueryOptions[fieldId]?.sortOrder
      if (sortOrderTextOption !== '') {
        const sortOrderString = sortOrderTextOption === 'asc' ? t`Ascending` : t`Descending`
        if (governanceType === 'property') {
          trackEvent(EventTrackingEventType.COLUMN_SORTED, {
            fieldType,
            sortOrder: sortOrderString,
            rowPropertyName,
          })
        } else {
          trackEvent(EventTrackingEventType.COLUMN_SORTED, {
            fieldType,
            sortOrder: sortOrderString,
          })
        }
      }
      // end Pendo event tracking logic
    },
    [fieldId, filters, onClose, sortOrder, search, updateQueryOptionsByFieldId],
  )

  useEffect(() => {
    resetColumnQueryOptions()
  }, [resetColumnQueryOptions])

  const componentByColumnDataType = (index: number, filter) => ({
    [ColumnDataType.SUBMISSION]: (
      <SelectSubmission
        filterIndex={index}
        filterValue={filter.value as SubmissionPartial}
        name={fieldId as DbColumnName}
        operator={filter.operator}
        onChange={handleFilterValueChange}
      />
    ),
    [ColumnDataType.USER]: (
      <SelectUser
        filterIndex={index}
        filterValue={filter.value as User}
        name={fieldId as DbColumnName}
        operator={filter.operator}
        userOptions={userOptions}
        onChange={handleFilterValueChange}
      />
    ),
    [ColumnDataType.DATE]: (
      <SelectDate
        filterIndex={index}
        filterValue={filter.value}
        operator={filter.operator as DateOperator}
        onChange={handleFilterValueChange}
      />
    ),
  })

  return (
    <Popover
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      open
      onClose={handleClose}
    >
      <css.PopoverContent onSubmit={applyColumnQueryOptions}>
        <Box px={2} py={1}>
          <TextField
            name="search"
            label={t`Search`}
            margin="dense"
            variant="outlined"
            fullWidth
            onChange={(e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
              handleSearch(e)
            }}
            value={search}
            placeholder={t`Search`}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <ClearIcon style={{ cursor: 'pointer' }} onClick={handleClearSearch} />
                </InputAdornment>
              ) : null,
            }}
          />
        </Box>

        <Divider />
        <List aria-label="Column sort order">
          <ListItem
            button
            selected={sortOrder === sortOrders.ASC}
            onClick={handleSortChange(sortOrders.ASC)}
          >
            <ListItemText primary={sortOrderText[sortOrders.ASC]} />
          </ListItem>
          <ListItem
            button
            selected={sortOrder === sortOrders.DESC}
            onClick={handleSortChange(sortOrders.DESC)}
          >
            <ListItemText primary={sortOrderText[sortOrders.DESC]} />
          </ListItem>
        </List>
        <Divider />
        <div data-testid="column-filter-popover-wrapper">
          {filters.length ? (
            filters.map((filter, index) => (
              <css.FilterListItem key={filter.id} data-testid="column-filter-list-item">
                <ComparisonOperatorSelect
                  columnDataType={columnDataType}
                  FormControlComponent={css.ComparisonOperatorFormControl}
                  value={filter.operator}
                  onChangeSelect={handleFilterOperatorChange(index)}
                  operatorFilter={operatorConfig => operatorConfig.canHaveColumnOperand()}
                  size="small"
                  data-testid="operator-selector"
                />
                {componentByColumnDataType(index, filter)?.[columnDataType] || (
                  <ComparisonOperatorValueField
                    filterIndex={index}
                    filterValue={filter.value}
                    operator={filter.operator}
                    onChange={handleFilterValueChange}
                    data-testid="operator-value-filed"
                  />
                )}
                <css.FilterListItemButtons data-testid="button-container">
                  <css.RemoveFab
                    size="small"
                    onClick={removeFilter(index)}
                    data-testid="remove-fab-button"
                  >
                    <RemoveIcon fontSize="small" />
                  </css.RemoveFab>
                  <css.AddFab
                    color="secondary"
                    size="small"
                    onClick={addFilter(index + 1)}
                    disabled={filters.length === MAX_FILTER_COUNT}
                    data-testid="add-fab-button"
                  >
                    <AddIcon fontSize="small" />
                  </css.AddFab>
                </css.FilterListItemButtons>
              </css.FilterListItem>
            ))
          ) : (
            <ListItem button onClick={addFilter(0)} data-testid="column-filter-list-item">
              <css.FilterListItemIcon data-testid="list-item-icon">
                <AddIcon />
              </css.FilterListItemIcon>
              <ListItemText primary={t`Add Filter`} data-testid="list-item-text-add-filter" />
            </ListItem>
          )}
        </div>
        <Divider />
        <css.ActionButtonsContainer>
          <Button onClick={clearColumnQueryOptions} data-testid="filter-popover-reset-button">
            {t`Reset`}
          </Button>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            data-testid="filter-popover-apply-button"
          >
            {t`Apply`}
          </Button>
        </css.ActionButtonsContainer>
      </css.PopoverContent>
    </Popover>
  )
}
